import { useEffect, type ReactElement } from "react";
import { useGameStore } from "./store/useGameStore";
import { Layout } from "./components/Layout";
import { MainScreen } from "./screens/MainScreen";
import { BattleScreen } from "./screens/BattleScreen";
import { CampaignRunScreen } from "./screens/CampaignRunScreen";
import { HeroScreen } from "./screens/HeroScreen";
import { ShopScreen } from "./screens/ShopScreen";
import { PremiumScreen } from "./screens/PremiumScreen";
import { CampaignScreen } from "./screens/CampaignScreen";
import { SkillTreeScreen } from "./screens/SkillTreeScreen";
import { InventoryScreen } from "./screens/InventoryScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { AdminGrantsScreen } from "./screens/AdminGrantsScreen";
import { AdminCharacterScreen } from "./screens/AdminCharacterScreen";
import { TopScreen } from "./screens/TopScreen";
import { DebugScreen } from "./screens/DebugScreen";
import { AstralScreen } from "./screens/AstralScreen";
import { DuelsScreen } from "./screens/DuelsScreen";
import { WorldBossScreen } from "./screens/WorldBossScreen";
import { CatacombsScreen } from "./screens/CatacombsScreen";
import { BlacksmithScreen } from "./screens/BlacksmithScreen";
import { MpDungeonsScreen } from "./screens/MpDungeonsScreen";
import { MpDungeonRunScreen } from "./screens/MpDungeonRunScreen";
import { MineScreen } from "./screens/MineScreen";
import { GuildScreen } from "./screens/GuildScreen";
import { GuildTreeScreen } from "./screens/GuildTreeScreen";
import { ArenaScreen } from "./screens/ArenaScreen";
import { RouletteScreen } from "./screens/RouletteScreen";
import { MountsScreen } from "./screens/MountsScreen";
import { CharacterCreationScreen } from "./screens/CharacterCreationScreen";
import { LevelUpModal } from "./components/LevelUpModal";
import { LootDropModal } from "./components/LootDropModal";
import { initTelegram, hapticSuccess } from "./utils/telegram";
import type { Screen } from "./types";
import { useDuelStore } from "./store/useDuelStore";
import { useWorldBossStore } from "./store/useWorldBossStore";
import { useMpDungeonStore } from "./store/useMpDungeonStore";
import { useMineStore } from "./store/useMineStore";
import { useGuildStore } from "./store/useGuildStore";

export function App() {
  const {
    init,
    isLoading,
    isAuthenticated,
    needsCharacterCreation,
    activeScreen,
    error,
    hero,
    pendingLevelUp,
    pendingLootQueue,
    dismissLevelUp,
    popLoot,
    setScreen,
  } = useGameStore();

  useEffect(() => {
    if (pendingLevelUp) hapticSuccess();
  }, [pendingLevelUp]);

  useEffect(() => {
    initTelegram();
    init();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || needsCharacterCreation) return;
    void useDuelStore.getState().poll();
    void useWorldBossStore.getState().pollStatus();
    void useMpDungeonStore.getState().poll();
    void useMineStore.getState().refresh();
    void useGuildStore.getState().sendPresence();
    const timer = setInterval(() => {
      void useDuelStore.getState().poll();
      void useWorldBossStore.getState().pollStatus();
      void useMpDungeonStore.getState().poll();
      void useGuildStore.getState().sendPresence();
      if (useGameStore.getState().activeScreen === "mine") {
        void useMineStore.getState().refresh();
      }
    }, 2500);
    const presenceTimer = setInterval(() => {
      void useGuildStore.getState().sendPresence();
    }, 60000);
    return () => {
      clearInterval(timer);
      clearInterval(presenceTimer);
    };
  }, [isAuthenticated, needsCharacterCreation]);

  if (isLoading) {
    return (
      <div className="h-screen bg-kingdom-dark flex flex-col items-center justify-center gap-4">
        <div className="text-6xl animate-bounce">⚔️</div>
        <p className="text-kingdom-glow text-lg font-bold animate-pulse">Loading Kingdoms...</p>
      </div>
    );
  }

  if (error && !isAuthenticated) {
    return (
      <div className="h-screen bg-kingdom-dark flex flex-col items-center justify-center gap-4 p-6">
        <div className="text-5xl">❌</div>
        <p className="text-rose-400 text-center">{error}</p>
        <p className="text-gray-500 text-sm text-center">
          Open the app via Telegram
        </p>
      </div>
    );
  }

  if (isAuthenticated && needsCharacterCreation) {
    return <CharacterCreationScreen />;
  }

  const screens: Record<Screen, ReactElement> = {
    main: <MainScreen />,
    battle: <BattleScreen />,
    top: <TopScreen />,
    hero: <HeroScreen />,
    shop: <ShopScreen />,
    premium: <PremiumScreen />,
    campaign: <CampaignScreen />,
    campaignRun: <CampaignRunScreen />,
    skills: <SkillTreeScreen />,
    inventory: <InventoryScreen />,
    astral: <AstralScreen />,
    duels: <DuelsScreen />,
    worldBoss: <WorldBossScreen />,
    catacombs: <CatacombsScreen />,
    blacksmith: <BlacksmithScreen />,
    mpDungeons: <MpDungeonsScreen />,
    mpDungeonRun: <MpDungeonRunScreen />,
    mine: <MineScreen />,
    guild: <GuildScreen />,
    guildTree: <GuildTreeScreen />,
    arena: <ArenaScreen />,
    roulette: <RouletteScreen />,
    mounts: <MountsScreen />,
    admin: <AdminScreen />,
    adminCharacter: <AdminCharacterScreen />,
    adminGrants: <AdminGrantsScreen />,
    debug: <DebugScreen />,
  };

  return (
    <>
      <Layout>{screens[activeScreen]}</Layout>
      <DuelExpiredModal />
      <MpDungeonExpiredModal />
      {hero && pendingLootQueue[0] && (
        <LootDropModal item={pendingLootQueue[0]} heroClass={hero.hero_class} onDismiss={popLoot} />
      )}
      {pendingLevelUp && pendingLootQueue.length === 0 && (
        <LevelUpModal
          info={pendingLevelUp}
          onDismiss={dismissLevelUp}
          onGoToHero={() => {
            dismissLevelUp();
            setScreen("hero");
          }}
        />
      )}
    </>
  );
}

function MpDungeonExpiredModal() {
  const { expiredModal, dismissExpiredModal } = useMpDungeonStore();
  if (!expiredModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6">
      <div className="w-full max-w-sm rounded-2xl bg-kingdom-purple border-2 border-amber-500/50 p-6 text-center space-y-4">
        <div className="text-5xl">⏱</div>
        <h2 className="text-lg font-bold font-game text-gold-400">MP Dungeon cancelled</h2>
        <p className="text-gray-300 text-sm leading-relaxed">{expiredModal}</p>
        <button
          type="button"
          onClick={dismissExpiredModal}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold active:scale-95"
        >
          OK
        </button>
      </div>
    </div>
  );
}

function DuelExpiredModal() {
  const { expiredModal, dismissExpiredModal } = useDuelStore();
  if (!expiredModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6">
      <div className="w-full max-w-sm rounded-2xl bg-kingdom-purple border-2 border-amber-500/50 p-6 text-center space-y-4">
        <div className="text-5xl">⏱</div>
        <h2 className="text-lg font-bold font-game text-gold-400">Duel cancelled</h2>
        <p className="text-gray-300 text-sm leading-relaxed">{expiredModal}</p>
        <button
          type="button"
          onClick={dismissExpiredModal}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold active:scale-95"
        >
          OK
        </button>
      </div>
    </div>
  );
}
